import React, { useState, useEffect } from 'react';
import { UserService, UpdateUserRequest, UserListItem } from '../services/userService';
import { Modal } from './Modal';
import '../styles/modals.css';

interface EditUserModalProps {
  isOpen: boolean;
  user: UserListItem | null;
  onClose: () => void;
  onSuccess: (user: UserListItem) => void;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    first_name: string;
    last_name: string;
    role: 'admin' | 'clinician' | 'reception' | 'receptionist' | 'pa' | 'manager' | 'viewer' | 'pending';
    status: 'active' | 'inactive' | 'suspended' | 'pending_verification';
    department: string;
  }>({
    first_name: '',
    last_name: '',
    role: 'clinician',
    status: 'active',
    department: '',
  });

  // Initialize form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: (user.role as 'admin' | 'clinician' | 'reception' | 'receptionist' | 'pa' | 'manager' | 'viewer' | 'pending') || 'clinician',
        status: (user.status as 'active' | 'inactive' | 'suspended' | 'pending_verification') || 'active',
        department: user.department || '',
      });
      setError(null);
    }
  }, [user]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      setError('User not found');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const updateData: UpdateUserRequest = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        status: formData.status,
        ...(formData.department && { department: formData.department }),
      };

      const updatedUser = await UserService.updateUser(user.id, updateData);
      
      onSuccess(updatedUser);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!isOpen || !user) {
    return null;
  }

  const roleChanged = formData.role !== user.role;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      preventCloseOnOverlayClick={loading}
      style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
    >
      <div className="modal-header" style={{ flexShrink: 0 }}>
        <h2>Edit Staff Member</h2>
        <button
          className="modal-close"
          onClick={handleClose}
          aria-label="Close modal"
        >
          ×
        </button>
      </div>

      <div className="modal-body" style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {error && (
          <div className="alert alert-danger">
            {error}
            </div>
          )}

          {roleChanged && (
            <div className="alert alert-warning" style={{ marginBottom: '20px' }}>
              <strong>⚠️ Role Change Warning:</strong> Changing a user's role will require them to log out and log back in for the changes to take effect. Their current session will continue with the old role until they re-authenticate.
            </div>
          )}

          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-group">
              <label htmlFor="edit_first_name">First Name *</label>
              <input
                type="text"
                id="edit_first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="John"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit_last_name">Last Name *</label>
              <input
                type="text"
                id="edit_last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Doe"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit_email">Email Address</label>
              <input
                type="email"
                id="edit_email"
                value={user.email}
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
              <small style={{ color: '#666', fontSize: '0.9em' }}>
                Email cannot be changed
              </small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit_role">Role</label>
                <select
                  id="edit_role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="admin">Admin</option>
                  <option value="clinician">Clinician</option>
                  <option value="reception">Reception</option>
                  <option value="receptionist">Receptionist</option>
                  <option value="pa">PA</option>
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="edit_status">Status</label>
                <select
                  id="edit_status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={loading}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending_verification">Pending Verification</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="edit_department">Department (Optional)</label>
              <input
                type="text"
                id="edit_department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                placeholder="e.g., Orthopedics, Physiotherapy"
                disabled={loading}
              />
            </div>
          </form>
        </div>

        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update User'}
          </button>
        </div>
      </Modal>
  );
};

